<script>
import { AvailabilityApi } from '../../api/AvailabilityApi'
import VueCal from 'vue-cal'
import 'vue-cal/dist/vuecal.css'
import { DateTime, Duration } from 'luxon'

export default {
    name: 'Availabilities',
    components: { VueCal },

    props: {
        applicant: {
            type: Object,
            default() {
                null
            },
        },
        canDelete: {
            type: Boolean,
            default: false,
        },
        role: {
            type: String,
            default: 'applicant',
        },
        context: {
            type: String,
            default() {
                null
            },
        },
    },
    data() {
        return {
            dateNow: DateTime.now().toJSDate(),
            selectedDate: DateTime.now().toJSDate(),
            events: [],
            mobileScreen: '',
            showDelete: false,
            selectedEvent: null,
            weeklyHours: {},
        }
    },
    mounted() {
        this.getEvents()
        this.isMobile()
    },
    methods: {
        formatHour(date) {
            if (!date) return ''
            const d = new Date(date)
            return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', hour12: false })
        },
        getEvents() {
            this.selectedEvent = null
            const params = {
                start: this.$refs.calendar.view.startDate,
                end: this.$refs.calendar.view.endDate,
            }
            let promise
            if (this.role === 'admin') {
                promise = AvailabilityApi.indexAsAdmin(this.applicant, params)
            } else if (this.role === 'backoffice') {
                promise = AvailabilityApi.indexAsBackoffice(this.applicant, params)
            } else {
                promise = AvailabilityApi.indexAsApplicant(params)
            }
            promise
                .then(data => {
                    this.events = data.map(event => {
                        if (event.advanced_planning === false) {
                            event.class = 'bg-dark-raspberry'
                        } else {
                            event.class = 'bg-finger-banana'
                        }
                        return event
                    })
                    this.weeklyHours = this.calculateWeeklyHours(this.events)
                })
                .finally(() => {
                    this.loading = false
                })
        },
        selectDate(e) {
            this.selectedDate = e
        },
        isMobile() {
            this.mobileScreen = window.screen.width <= 760
        },
        onEventDelete() {
            if (this.selectedEvent.type !== 'unavailable') {
                alert('Vous ne pouvez pas supprimer une indisponibilité liée à un contrat')
            } else {
                const promise =
                    this.role === 'admin'
                        ? AvailabilityApi.destroyAsAdmin(this.applicant, this.selectedEvent.id)
                        : AvailabilityApi.destroyAsApplicant(this.selectedEvent.id)
                promise.then(() => {
                    this.getEvents()
                    this.selectedEvent = null
                })
            }
        },
        onEventFocus(e) {
            if (e.type === 'unavailable') {
                this.selectedEvent = e
            } else {
                this.selectedEvent = null
            }
        },
        calculateWeeklyHours(events) {
            const weeklyHours = {}

            events.forEach(event => {
                let start = DateTime.fromFormat(event.start, 'yyyy-MM-dd HH:mm')
                const end = DateTime.fromFormat(event.end, 'yyyy-MM-dd HH:mm')
                const weekNumber = start.weekNumber

                if (event.advanced_planning === false) {
                    if (!weeklyHours[weekNumber]) {
                        weeklyHours[weekNumber] = {
                            hasAbsence: true,
                            isOverMaxTime: false,
                        }
                    }
                    return
                }

                if (weeklyHours[weekNumber]?.hasAbsence) {
                    return
                }

                if (!event.advanced_planning) {
                    weeklyHours[weekNumber] = {
                        hasAbsence: false,
                        time: 'Planning non renseigné',
                        isOverMaxTime: false,
                    }
                    return
                }

                while (start < end) {
                    const currentWeekNumber = start.weekNumber
                    const endOfDay = end < start.endOf('day') ? end : start.endOf('day')
                    const currentWeekHours = weeklyHours[currentWeekNumber]

                    if (currentWeekHours === undefined || currentWeekHours instanceof Duration) {
                        const duration = endOfDay.diff(start, ['hours', 'minutes'])
                        const roundedDuration = {
                            hours: duration.hours,
                            minutes: Math.round(duration.minutes),
                        }
                        weeklyHours[currentWeekNumber] = (
                            currentWeekHours || Duration.fromObject({ hours: 0, minutes: 0 })
                        ).plus(Duration.fromObject(roundedDuration))
                    }

                    start = start.plus({ days: 1 }).startOf('day')
                }
            })

            Object.keys(weeklyHours).forEach(week => {
                if (!weeklyHours[week].hasAbsence) {
                    const durationWeek = weeklyHours[week].shiftTo('hours', 'minutes')
                    const totalHours = durationWeek.hours + durationWeek.minutes / 60

                    weeklyHours[week] = {
                        hasAbsence: false,
                        time:
                            durationWeek.minutes === 0
                                ? `${durationWeek.hours}h00 prévues au planning`
                                : `${durationWeek.hours}h${durationWeek.minutes} prévues au planning`,
                        class: totalHours >= 35 ? 'bg-fuel-yellow text-white' : 'bg-dugong--lighter text-dark',
                    }
                }
            })
            return weeklyHours
        },
    },
}
</script>

<template>
    <div class="col-12 d-flex mb-5">
        <div class="col-12 h-100 box-shadow" :class="[context ? 'col-md-12' : 'col-md-6']">
            <vue-cal
                ref="calendar"
                class="vuecal--rounded-theme bg-white card shadow-none vuecal--full-height-delete cursor-pointer border-radius-bottom-0"
                :class="[context ? '' : 'h-500']"
                :selected-date="dateNow"
                xsmall
                show-week-numbers
                locale="fr"
                :time-from="0"
                :disable-views="['years', 'year', 'week']"
                active-view="month"
                :events="events"
                :editable-events="{
                    title: false,
                    drag: false,
                    resize: false,
                    delete: true,
                    editable: false,
                }"
                events-on-month-views
                :dblclick-to-navigate="mobileScreen"
                :hide-view-selector="!mobileScreen || context"
                @cell-click="selectDate"
                @view-change="getEvents"
                @event-delete="onEventDelete"
                @event-mouse-enter="onEventFocus"
            >
                <template #arrow-prev>
                    <i class="fa-solid fa-chevron-left color-fuel-yellow"></i>
                </template>
                <template #arrow-next>
                    <i class="fa-solid fa-chevron-right color-fuel-yellow"></i>
                </template>
                <template #events-count="{ events: dayEvents }">
                    <span
                        :class="
                            dayEvents.find(event => event.advanced_planning === false)?.class || dayEvents[0]?.class
                        "
                        class="vuecal__cell-events-count mt-3"
                    ></span>
                </template>
                <template v-if="weeklyHours" #week-number-cell="{ week }">
                    <div class="week-number-cell align-items-end">
                        <div v-if="weeklyHours[week]" :class="weeklyHours[week]?.class" class="text-center w-100">
                            {{ weeklyHours[week].time }}
                        </div>
                    </div>
                </template>
            </vue-cal>
            <div class="d-flex justify-content-center mt-n5">
                <button
                    v-show="selectedEvent && mobileScreen"
                    type="button"
                    class="btn btn-dark-raspberry rounded-circle p-2 btn-add border-white"
                    @click="onEventDelete()"
                >
                    <i class="fa-solid fa-trash"></i>
                </button>
            </div>
            <div v-if="!context" class="card mt-5 shadow-none p-3 border-radius-top-0">
                <div class="card mt-3 shadow-none p-3">
                    <div class="d-flex align-items-center">
                        <span class="h1 m-0 color-dark-raspberry pr-1">•</span>Indisponible
                    </div>
                    <div class="d-flex align-items-center">
                        <span class="h1 m-0 color-finger-banana pr-1">•</span>Indisponible en contrat Ettic
                    </div>
                </div>
            </div>
        </div>
        <div v-if="!context" class="col-md-6 d-none d-md-block box-shadow ml-3">
            <vue-cal
                ref="dayCalendar"
                class="vuecal--rounded-theme bg-white card shadow-none vuecal--full-height-delete h-500"
                :selected-date="selectedDate"
                :disable-views="['years', 'year', 'month', 'week']"
                small
                locale="fr"
                :time-from="0"
                active-view="day"
                :events="events"
                hide-view-selector
                show-all-day-events
                :editable-events="{ title: false, drag: false, delete: true, resize: false }"
                @view-change="getEvents"
                @event-delete="onEventDelete"
                @event-mouse-enter="onEventFocus"
            >
                <template #event="{ event }">
                    {{
                        event.advanced_planning === false
                            ? event.content || 'Absence'
                            : `${formatHour(event.start)} - ${formatHour(event.end)} / ${event.organization_name} - ${event.center_name}`
                    }}
                </template>
                <template #arrow-prev>
                    <i class="fa-solid fa-chevron-left color-fuel-yellow"></i>
                </template>
                <template #arrow-next>
                    <i class="fa-solid fa-chevron-right color-fuel-yellow"></i>
                </template>
            </vue-cal>
            <div class="d-flex justify-content-center mt-n5">
                <button
                    v-show="selectedEvent && canDelete"
                    type="submit"
                    class="btn btn-dark-raspberry rounded-circle p-2 btn-add border-white"
                    @click="onEventDelete()"
                >
                    <i class="fa-solid fa-trash"></i>
                </button>
            </div>
        </div>
    </div>
</template>

<style>
.border-radius-top-0 {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
}

.border-radius-bottom-0 {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
}

.vuecal--month-view .vuecal__bg {
    overflow: hidden;
}

.vuecal__cell-events-count {
    color: transparent !important;
}

.vuecal__cell-events-count span {
    width: 10px;
}

.vuecal__cell-events-count:not(span) {
    background-color: transparent;
    height: 10px;
    width: 10px;
}

.vuecal__menu {
    margin-bottom: 10px;
    background-color: white;
    @media screen and (max-width: 575px) {
        display: none;
    }
}

.vuecal__cell--today,
.vuecal__cell--current {
    background-color: #aba3ae;
}

.vuecal:not(.vuecal--day-view) .vuecal__cell--selected .vuecal__cell-content {
    background-color: #fcf5e7;
    height: 25px;
    width: 25px;
}

.vuecal__cell--selected:before {
    border-color: #148181;
}

.weekday-label {
    font-weight: bold;
}

.vuecal__title-bar {
    background-color: transparent;
    padding-top: 10px;
}

.vuecal__cells.month-view .vuecal__cell {
    min-height: 60px;
}

.vuecal__event-time {
    display: none;
}

.vuecal__event-title,
.vuecal__event {
    color: white;
}

.vuecal__now-line {
    color: #d48f31;
    z-index: 10;
}

.vuecal__event--focus,
.vuecal__event:focus {
    box-shadow: 0px 0px 9px 2px #00000063;
    color: black;
}

#ProfileAvailabilities .box-shadow {
    box-shadow: 0 0 1.25rem rgba(0, 0, 0, 0.15);
    border-radius: 10px;
}

.vuecal__week-numbers {
    position: absolute;
    width: 100%;
    height: 100%;
}

.vuecal__week-number-cell {
    opacity: 1 !important;
    margin-top: 10px;
}

.week-number-cell {
    min-height: 2.5rem;

    display: flex;
    width: 100%;
    font-size: 0.7em;
    :is(div) {
        border-radius: 30px;
    }
}

.vuecal--week-numbers .vuecal__weekdays-headings {
    padding-left: 0px;
}
</style>
